<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Participantes</h1>
      <div class="header-actions">
        <!-- Sem gate de role: quem ve esta pagina (inclusive CHECKIN) pode
             imprimir a propria lista de cracha, do mesmo jeito que ja ve a
             tabela inteira. Exporta o que estiver FILTRADO na tela — filtrar
             por quarto antes de clicar imprime so os crachas daquele quarto. -->
        <button
          class="btn-secondary btn-exportar"
          :disabled="!filteredItems.length || exportando"
          @click="exportarCrachas"
        >{{ exportando ? 'Gerando…' : '📄 Crachás com QR' }}</button>
        <button v-if="authStore.canManageParticipants" class="btn-primary" @click="openCreate">+ Novo</button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-bar card">
      <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar por nome..." />
      <select class="input filter-select" v-model="quartoFilter">
        <option value="all">Todos os quartos</option>
        <option v-for="q in quartoOptions" :key="q" :value="q">{{ q }}</option>
      </select>
      <select class="input filter-select" v-model="statusFilter">
        <option value="all">Todos</option>
        <option value="pending">Pendentes</option>
        <option value="confirmed">Confirmados</option>
        <option value="no-contact">Sem contato</option>
      </select>
      <select class="input filter-select" v-model="origemFilter">
        <option value="all">Todas as origens</option>
        <option value="manual">Manual</option>
        <option value="auto">Auto-cadastro</option>
      </select>
    </div>

    <!-- Loading -->
    <SkeletonLoader v-if="store.loading" variant="table-row" :count="8" />

    <!-- Tabela -->
    <div class="table-card card" v-else-if="filteredItems.length > 0">
      <table class="table">
        <thead>
          <tr>
            <th>Nome</th>
            <th class="hide-mobile">Lider</th>
            <th class="hide-mobile">Celula</th>
            <th class="hide-mobile">Quarto</th>
            <th>Status</th>
            <th>Acoes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredItems" :key="item.id" class="clickable-row" :class="{ 'row-readonly': !authStore.canManageParticipants }" @click="onRowClick(item)">
            <td>
              <div class="name-wrap">
                <span class="name-cell">{{ item.name }}</span>
                <span v-if="item.autoCadastrado" class="auto-chip" title="Auto-cadastro via formulario publico">🌐 Auto</span>
              </div>
              <span class="sub-cell show-mobile">{{ item.leader }}</span>
            </td>
            <td class="hide-mobile">{{ item.leader }}</td>
            <td class="hide-mobile">{{ item.celula || '-' }}</td>
            <td class="hide-mobile">{{ item.quarto || '-' }}</td>
            <td><StatusBadge :status="item.check ? 'confirmed' : 'pending'" /></td>
            <td @click.stop>
              <div class="action-btns">
                <button v-if="authStore.canManageParticipants" class="btn-icon-sm btn-icon-sm-qr" @click="openQr(item)" title="QR Code">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm13-2h-2v3h-3v2h3v3h2v-3h3v-2h-3v-3zm-3 8v-3h-2v5h5v-2h-3zm6 0h-2v2h2v-2z"/></svg>
                </button>
                <button v-if="authStore.canManageParticipants" class="btn-icon-sm" @click="openEdit(item)" title="Editar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83l3.75 3.75l1.83-1.83z"/></svg>
                </button>
                <button v-if="authStore.canManageParticipants" class="btn-icon-sm btn-icon-sm-danger" @click="confirmDelete(item.id)" title="Remover">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12Z"/></svg>
                </button>
                <span v-if="!authStore.canManageParticipants" class="readonly-hint">leitura</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty -->
    <EmptyState
      v-else
      title="Nenhum participante"
      :description="emptyDescription"
      :actionLabel="authStore.canManageParticipants ? '+ Novo Participante' : null"
      @action="openCreate"
    />

    <!-- Form Modal -->
    <ParticipantForm
      :visible="formVisible"
      :participant="editingParticipant"
      @save="handleSave"
      @cancel="formVisible = false"
    />

    <!-- QR Code Modal -->
    <div class="modal-overlay" v-if="qrParticipant" @click.self="qrParticipant = null">
      <div class="modal-card qr-modal">
        <h2 class="modal-title">{{ qrParticipant.name }}</h2>
        <p class="qr-instruction">Envie este QR Code para o participante via WhatsApp ou imprima.</p>
        <QrCodeDisplay :value="qrParticipant.id" :size="200" :label="qrParticipant.name" />
        <div class="qr-scan-url">
          <span class="label">Link para scanner</span>
          <code class="qr-url">{{ scanUrl }}</code>
        </div>
        <div class="modal-actions">
          <button class="btn-whatsapp" @click="shareQrWhatsApp">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967c-.273-.099-.471-.148-.67.15c-.197.297-.767.966-.94 1.164c-.173.199-.347.223-.644.075c-.297-.15-1.255-.463-2.39-1.475c-.883-.788-1.48-1.761-1.653-2.059c-.173-.297-.018-.458.13-.606c.134-.133.298-.347.446-.52c.149-.174.198-.298.298-.497c.099-.198.05-.371-.025-.52c-.075-.149-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51c-.173-.008-.371-.01-.57-.01c-.198 0-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074c.149.198 2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625c.712.227 1.36.195 1.871.118c.571-.085 1.758-.719 2.006-1.413c.248-.694.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214l-3.741.982l.998-3.648l-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884c2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
            Enviar no WhatsApp
          </button>
          <button class="btn-secondary" @click="qrParticipant = null">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Confirm Delete -->
    <ConfirmDialog
      :visible="deleteId !== ''"
      title="Remover participante?"
      message="Esta acao nao pode ser desfeita."
      confirmLabel="Remover"
      @confirm="handleDelete"
      @cancel="deleteId = ''"
    />
  </div>
</template>

<script>
import { useParticipantsStore } from '../stores/participants'
import { useAuthStore } from '../stores/auth'
import { useEncontroStore } from '../stores/encontro'
import ParticipantForm from '../components/ParticipantForm.vue'
import StatusBadge from '../components/ui/StatusBadge.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'
import ConfirmDialog from '../components/ui/ConfirmDialog.vue'
import QrCodeDisplay from '../components/QrCodeDisplay.vue'

export default {
  name: 'ParticipantsPage',
  components: { ParticipantForm, StatusBadge, SkeletonLoader, EmptyState, ConfirmDialog, QrCodeDisplay },
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      quartoFilter: 'all',
      origemFilter: 'all',
      formVisible: false,
      editingParticipant: null,
      deleteId: '',
      qrParticipant: null,
      exportando: false
    }
  },
  computed: {
    store() { return useParticipantsStore() },
    authStore() { return useAuthStore() },
    encontroStore() { return useEncontroStore() },
    scanUrl() { return `${window.location.origin}/scan` },
    emptyDescription() {
      return this.authStore.canManageParticipants
        ? 'Cadastre o primeiro participante para comecar.'
        : 'Nenhum participante cadastrado ainda.'
    },
    quartoOptions() {
      const quartos = this.store.participants
        .map(p => p.quarto)
        .filter(q => q && q.trim())
      return [...new Set(quartos)].sort()
    },
    filteredItems() {
      let items = this.store.participants
      if (this.statusFilter === 'pending') items = items.filter(i => !i.check)
      if (this.statusFilter === 'confirmed') items = items.filter(i => i.check)
      if (this.statusFilter === 'no-contact') items = items.filter(i => !i.number && !i.numberFamily)
      if (this.quartoFilter !== 'all') items = items.filter(i => i.quarto === this.quartoFilter)
      if (this.origemFilter === 'auto') items = items.filter(i => i.autoCadastrado)
      if (this.origemFilter === 'manual') items = items.filter(i => !i.autoCadastrado)
      if (this.searchQuery.trim()) {
        const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        const q = normalize(this.searchQuery)
        items = items.filter(i => normalize(i.name).includes(q))
      }
      return items
    }
  },
  watch: {
    '$route.query.origem': {
      immediate: true,
      handler(v) {
        if (v === 'auto' || v === 'manual') this.origemFilter = v
        else this.origemFilter = 'all'
      }
    },
    origemFilter(v) {
      const next = v === 'all' ? undefined : v
      if (this.$route.query.origem === next) return
      this.$router.replace({ query: { ...this.$route.query, origem: next } })
    }
  },
  methods: {
    onRowClick(participant) {
      if (!this.authStore.canManageParticipants) return
      this.openEdit(participant)
    },
    openQr(participant) {
      this.qrParticipant = { ...participant }
    },
    /**
     * `jspdf`/`qrcode` so carregam aqui, no clique — nao no topo do arquivo.
     * O painel inteiro ainda divide bundle com as paginas publicas (main.js
     * importa tudo de cara), entao uma lib de PDF com gerador de QR so deve
     * pesar para quem de fato exporta.
     */
    async exportarCrachas() {
      if (!this.filteredItems.length || this.exportando) return
      this.exportando = true
      try {
        const { gerarPdfCrachas, nomeDoArquivo } = await import('../lib/exportarCrachasPdf')
        const doc = await gerarPdfCrachas({ participantes: this.filteredItems })
        doc.save(nomeDoArquivo(this.encontroStore.info))
      } catch {
        this.$toast.open({ message: 'Falha ao gerar o PDF', type: 'error', position: 'top-right', duration: 5000 })
      } finally {
        this.exportando = false
      }
    },
    openCreate() {
      if (!this.authStore.canManageParticipants) return
      this.editingParticipant = null
      this.formVisible = true
    },
    openEdit(participant) {
      if (!this.authStore.canManageParticipants) return
      this.editingParticipant = { ...participant }
      this.formVisible = true
    },
    async handleSave(payload) {
      const { data, newPhotos = [], removedPublicIds = [] } = payload
      try {
        if (this.editingParticipant) {
          const res = await this.store.update(this.editingParticipant.id, data, { newPhotos, removedPublicIds })
          const warn = res?.photoUploadWarning
          this.$toast.open({
            message: warn || 'Participante atualizado',
            type: warn ? 'warning' : 'success',
            position: 'top-right',
            duration: warn ? 5000 : 3000
          })
        } else {
          await this.store.create(data)
          this.$toast.open({ message: 'Participante cadastrado', type: 'success', position: 'top-right', duration: 3000 })
        }
        this.formVisible = false
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao salvar'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 5000 })
      }
    },
    async shareQrWhatsApp() {
      const participant = this.qrParticipant
      if (!participant) return

      const canvas = document.querySelector('.qr-modal canvas')
      if (!canvas) return

      try {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
        const file = new File([blob], `qrcode-${participant.name}.png`, { type: 'image/png' })

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `QR Code - ${participant.name}`,
            text: `QR Code de check-in de ${participant.name}`
          })
          return
        }
      } catch (err) {
        if (err.name === 'AbortError') return
      }

      // Fallback: abre WhatsApp com link do scan
      const phone = (participant.number || participant.numberFamily || '').replace(/\D/g, '')
      const text = encodeURIComponent(`Olá ${participant.name}! Segue seu link de check-in: ${this.scanUrl}`)
      const url = phone ? `https://wa.me/55${phone}?text=${text}` : `https://wa.me/?text=${text}`
      window.open(url, '_blank')
    },
    confirmDelete(id) {
      this.deleteId = id
    },
    async handleDelete() {
      try {
        await this.store.remove(this.deleteId)
        this.$toast.open({ message: 'Participante removido', type: 'info', position: 'top-right', duration: 3000 })
      } catch {
        this.$toast.open({ message: 'Erro ao remover', type: 'error', position: 'top-right', duration: 5000 })
      }
      this.deleteId = ''
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
}
.page-header .page-title { margin-bottom: 0; }
.header-actions { display: flex; gap: var(--spacing-sm); align-items: center; }
.btn-exportar { white-space: nowrap; }
@media (max-width: 540px) {
  .page-header { flex-wrap: wrap; gap: var(--spacing-sm); }
  .header-actions { width: 100%; }
  .header-actions button { flex: 1 1 auto; }
}
.filters-bar {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-base);
  margin-bottom: var(--spacing-base);
}
.search-input { flex: 1; }
.filter-select { max-width: 160px; }
.table-card { padding: 0; overflow-x: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
}
.table th {
  text-align: left;
  padding: 12px var(--spacing-base);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 14px var(--spacing-base);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-bg-separator);
}
.table tr:last-child td { border-bottom: none; }
.table tr.clickable-row { cursor: pointer; }
.table tr.row-readonly { cursor: default; }
.table tr:hover td { background: var(--color-bg-hover); }
.name-wrap { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.name-cell { font-weight: 600; }
.auto-chip {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--color-bg-body);
  color: var(--color-text-secondary);
}
.readonly-hint {
  font-size: 11px;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.sub-cell {
  display: none;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.action-btns { display: flex; gap: var(--spacing-sm); }
.btn-icon-sm {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-bg-hover);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}
.btn-icon-sm-qr:hover { background: #9b59b6; color: #fff; }
.btn-icon-sm:hover { background: var(--color-accent); color: #fff; }
.btn-icon-sm-danger:hover { background: var(--color-danger); color: #fff; }
@media (max-width: 600px) {
  .hide-mobile { display: none; }
  .sub-cell.show-mobile { display: block; }
  .filters-bar { flex-direction: column; }
  .filter-select { max-width: 100%; }
}
.qr-modal { max-width: 400px; text-align: center; }
.qr-instruction {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-base);
}
.qr-scan-url {
  margin-top: var(--spacing-base);
  text-align: left;
}
.btn-whatsapp {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 10px 20px;
  background: #25D366;
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}
.btn-whatsapp:hover { background: #1ebe57; }
.qr-url {
  display: block;
  padding: 8px 12px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  word-break: break-all;
  margin-top: var(--spacing-sm);
}
</style>
